import React, { useCallback, useState, useEffect, Fragment } from 'react';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { preventLeadingWhitespace } from '@/utils/helperFunctions';
import clsx from 'clsx';
import { textFieldValidation } from '@/utils/validations';
import { Container } from '@/components/container';
import {
  Toolbar,
  ToolbarDescription,
  ToolbarHeading,
  ToolbarPageTitle,
} from '@/partials/toolbar';
import { PageNavbar } from '@/pages/account';
import { useLayout } from '@/providers';
import {
  useGetPageQuery,
  useUpdateAboutUsContentMutation,
} from '@/services/CmsService';
import { languageOptions, PageType } from '@/types/cms.types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { setLanguage } from '@/store/authSlice';
import { ErrorApiResponse } from '@/types/api.types';
import { usePermissions } from '@/hooks/usePermissions';

interface AboutUsData {
  title: string;
  description: string;
  vision: string;
  mission: string;
  values: string;
  footerDescription: string;
}

const aboutUsValidationSchema = Yup.object().shape({
  title: textFieldValidation('Title', 1, 100, true),
  description: textFieldValidation('Description', 1, 500, true),
  vision: textFieldValidation('Vision', 1, 500, true),
  mission: textFieldValidation('Mission', 1, 500, true),
  values: textFieldValidation('Values', 1, 500, true),
  footerDescription: textFieldValidation('Footer Description', 1, 500, true),
});

const FormField: React.FC<{
  label: string;
  name: keyof AboutUsData;
  value: string;
  error: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  isTextArea?: boolean;
  maxLength: number;
  disabled?: boolean;
  autoComplete?: string;
}> = React.memo(
  ({
    label,
    name,
    value,
    error,
    onChange,
    isTextArea,
    maxLength,
    disabled,
    autoComplete = 'off',
  }) => {
    const id = `${name}-input`;
    return (
      <div>
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          {label}
        </label>
        {isTextArea ? (
          <>
            <textarea
              id={id}
              name={name}
              value={value}
              onChange={onChange}
              onKeyDown={preventLeadingWhitespace}
              maxLength={maxLength}
              rows={6}
              className={clsx(
                'input input-bordered w-full resize-none h-24 p-3',
                error && 'border-red-500 focus:ring-red-500'
              )}
              disabled={disabled}
              autoComplete={autoComplete}
              aria-invalid={!!error}
              aria-describedby={error ? `${name}-error` : undefined}
              placeholder={`Enter ${label.toLowerCase()} (max ${maxLength} characters)`}
            />
            <p className="text-xs text-gray-500 mt-1">
              {value?.length || 0}/{maxLength} characters
            </p>
          </>
        ) : (
          <input
            id={id}
            type="text"
            name={name}
            value={value}
            onChange={onChange}
            onKeyDown={preventLeadingWhitespace}
            maxLength={maxLength}
            disabled={disabled}
            autoComplete={autoComplete}
            className={clsx(
              'input input-bordered w-full h-10',
              error && 'border-red-500 focus:ring-red-500'
            )}
            aria-invalid={!!error}
            aria-describedby={error ? `${name}-error` : undefined}
            placeholder={`Enter ${label.toLowerCase()} (max ${maxLength} characters)`}
          />
        )}
        {error && (
          <p id={`${name}-error`} className="text-red-500 text-sm mt-1">
            {error}
          </p>
        )}
      </div>
    );
  }
);

const AboutUs = () => {
  const { currentLayout } = useLayout();
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState<Partial<AboutUsData>>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useDispatch();
  const language = useSelector((state: RootState) => state.auth.language);
  const { hasPermission } = usePermissions();

  const {
    data: pageData,
    isLoading: isFetching,
    error: fetchError,
  } = useGetPageQuery({ pageType: PageType.ABOUT_US, language });

  const [updateAboutUsContent, { isLoading: isUpdating }] =
    useUpdateAboutUsContentMutation();

  useEffect(() => {
    if (pageData?.data?.[0]) {
      const content = pageData.data[0].content;
      setFormData({
        title: content.heroBanner?.title || '',
        description: content.heroBanner?.description || '',
        vision: content.vision || '',
        mission: content.mission || '',
        values: content.values || '',
        footerDescription: content.footerDescription || '',
      });
    }
  }, [pageData]);

  const validateField = useCallback(
    async (name: keyof AboutUsData, value: string) => {
      try {
        await aboutUsValidationSchema.validateAt(name, { [name]: value });
        setErrors((prev) => ({ ...prev, [name]: '' }));
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          setErrors((prev) => ({ ...prev, [name]: err.message }));
        }
      }
    },
    []
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
      validateField(name as keyof AboutUsData, value);
    },
    [validateField]
  );

  const handleLanguageChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      dispatch(setLanguage(e.target.value)); // Fixed typo: setLanguagetsx -> setLanguage
    },
    [dispatch]
  );

  const handleCancelClick = useCallback(() => {
    setFormData(
      pageData?.data?.[0]?.content
        ? {
            title: pageData.data[0].content.heroBanner?.title || '',
            description: pageData.data[0].content.heroBanner?.description || '',
            vision: pageData.data[0].content.vision || '',
            mission: pageData.data[0].content.mission || '',
            values: pageData.data[0].content.values || '',
            footerDescription: pageData.data[0].content.footerDescription || '',
          }
        : {}
    );
    setErrors({});
  }, [pageData]);

  const handleSubmit = useCallback(async () => {
    try {
      await aboutUsValidationSchema.validate(formData, { abortEarly: false });
      if (!pageData?.data?.[0]?.id) {
        enqueueSnackbar('No page data available to update', {
          variant: 'error',
        });
        return;
      }
      setIsSubmitting(true);
      const payload = {
        pageId: pageData.data[0].id,
        body: {
          content: {
            heroBanner: {
              title: formData.title || '',
              description: formData.description || '',
            },
            vision: formData.vision || '',
            mission: formData.mission || '',
            values: formData.values || '',
            footerDescription: formData.footerDescription || '',
          },
        },
        language,
      };

      const response = await updateAboutUsContent(payload).unwrap();
      enqueueSnackbar(response.message, { variant: 'success' });
      setErrors({});
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const newErrors: { [key: string]: string } = {};
        err.inner.forEach((error) => {
          if (error.path) newErrors[error.path] = error.message;
        });
        setErrors(newErrors);
        enqueueSnackbar(
          `Validation failed: ${Object.values(newErrors).join(', ')}`,
          {
            variant: 'error',
          }
        );
      } else if (err as ErrorApiResponse) {
        enqueueSnackbar((err as ErrorApiResponse).data.message, {
          variant: 'error',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [enqueueSnackbar, formData, language, pageData, updateAboutUsContent]);

  if (isFetching) {
    return (
      <div className="card card-grid min-w-full">
        <div className="card-body">
          <div className="text-center py-4" role="status" aria-live="polite">
            Loading About Us...
          </div>
        </div>
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="card card-grid min-w-full">
        <div className="card-body">
          <div
            className="text-center py-4 text-red-500"
            role="alert"
            aria-live="polite"
          >
            Failed to load About Us content. Please try again.
          </div>
        </div>
      </div>
    );
  }

  const fields: Array<{
    name: keyof AboutUsData;
    label: string;
    isTextArea: boolean;
    maxLength: number;
    autoComplete: string;
  }> = [
    {
      name: 'title',
      label: 'Title',
      isTextArea: false,
      maxLength: 100,
      autoComplete: 'off',
    },
    {
      name: 'description',
      label: 'Description',
      isTextArea: true,
      maxLength: 500,
      autoComplete: 'off',
    },
    {
      name: 'vision',
      label: 'Vision',
      isTextArea: true,
      maxLength: 500,
      autoComplete: 'off',
    },
    {
      name: 'mission',
      label: 'Mission',
      isTextArea: true,
      maxLength: 500,
      autoComplete: 'off',
    },
    {
      name: 'values',
      label: 'Values',
      isTextArea: true,
      maxLength: 500,
      autoComplete: 'off',
    },
    {
      name: 'footerDescription',
      label: 'Footer Description',
      isTextArea: true,
      maxLength: 500,
      autoComplete: 'off',
    },
  ];

  if (!hasPermission('cms_management_read')) {
    return (
      <Fragment>
        <PageNavbar />
        {currentLayout?.name === 'demo1-layout' && (
          <Container>
            <Toolbar>
              <ToolbarHeading>
                <ToolbarPageTitle />
                <ToolbarDescription>
                  Manage your About Us content
                </ToolbarDescription>
              </ToolbarHeading>
            </Toolbar>
            <div className="bg-gray-100 p-6 rounded-lg shadow-lg flex flex-col">
              <div className="text-center py-4 text-gray-500">
                You don't have permission to view About Us content.
              </div>
            </div>
          </Container>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <PageNavbar />
      {currentLayout?.name === 'demo1-layout' && (
        <Container>
          <Toolbar>
            <ToolbarHeading>
              <ToolbarPageTitle id="about-us-heading" />
              <ToolbarDescription>
                Manage your About Us content
              </ToolbarDescription>
            </ToolbarHeading>
            <div className="relative">
              <select
                id="language-select"
                value={language}
                onChange={handleLanguageChange}
                className="select select-bordered pl-8 pr-8 h-10 leading-normal"
                aria-label="Select language"
              >
                {languageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <img
                src="/media/flags/globe.svg"
                alt="Globe"
                className="absolute dark:brightness-0 dark:invert contrast:brightness-0 dark:invert left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 invert pointer-events-none"
                aria-hidden="true"
              />
            </div>
          </Toolbar>
          <div className="bg-gray-100 p-6 rounded-lg shadow-lg flex flex-col">
            <div className="flex-1">
              <div className="flex flex-col gap-4">
                {/* Title in half section */}
                <div className="md:w-1/2">
                  <FormField
                    label="Title"
                    name="title"
                    value={formData.title || ''}
                    error={errors.title || ''}
                    onChange={handleInputChange}
                    isTextArea={false}
                    maxLength={100}
                    autoComplete="off"
                    disabled={isSubmitting || isUpdating}
                  />
                </div>
                {/* Other fields two per row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {fields
                    .filter((field) => field.name !== 'title')
                    .map(
                      ({
                        name,
                        label,
                        isTextArea,
                        maxLength,
                        autoComplete,
                      }) => (
                        <FormField
                          key={name}
                          label={label}
                          name={name}
                          value={formData[name] || ''}
                          error={errors[name] || ''}
                          onChange={handleInputChange}
                          isTextArea={isTextArea}
                          maxLength={maxLength}
                          autoComplete={autoComplete}
                          disabled={isSubmitting || isUpdating}
                        />
                      )
                    )}
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={handleCancelClick}
                className="btn btn-sm btn-light"
                aria-label="Cancel"
                disabled={isSubmitting || isUpdating}
              >
                Cancel
              </button>
              {hasPermission('cms_management_update') && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="btn btn-sm btn-primary"
                  aria-label="Save Changes"
                  disabled={isSubmitting || isUpdating}
                >
                  {isSubmitting || isUpdating ? 'Saving...' : 'Save'}
                </button>
              )}
            </div>
          </div>
        </Container>
      )}
    </Fragment>
  );
};

export { AboutUs };
